import { Dispatch, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import * as fabric from "fabric";
import { SetStateAction } from "react";
import { editorInterface } from "store/slice/editor";
import { BackgroundProps } from "../@types/background";
import { ImgCard, ParamGeneral, ServerResponse, TransformedData } from "../@types/data";
import { TextboxState } from "../@types/textbox";
import store from "../store";
import { configureFabricImage } from "./Canvas/createCanvasImage";
import { configureTextBox } from "./Canvas/createCanvasText";
import { backgroundCreation } from "../utils/backgroundCreation";


/**
 * Extends the `fabric.Object` class to include additional properties in the serialized object output.
 * This method modifies the `toObject` function of `fabric.Object` to always include the `idx` property
 * along with any custom properties specified.
 *
 * @return {void} This function does not return a value as it modifies the `fabric.Object` prototype directly.
 */
export function extendFabricToObject(): void {
  fabric.Object.prototype.toObject = (function ( toObject ) {
    return function ( propertiesToInclude?: string[] ) {
      // Always include 'idx' property and any additional ones passed in `propertiesToInclude`
      return toObject.call( this, [ "idx" ].concat( propertiesToInclude || [] ) );
    };
  })( fabric.Object.prototype.toObject );
}

/**
 * Handles the display of the toolbar by dispatching actions to update the state.
 *
 * @param {Function} dispatch - The dispatch function to send actions to the Redux store.
 * @param {string} value - The value used to determine the type of toolbar to display (e.g., "layout").
 * @param {number} [index] - Optional. The index used for specifying additional context for the toolbar display.
 * @return {void} No return value.
 */
export function handleDisplayToolbar(
  dispatch: ThunkDispatch<{ editor: editorInterface; }, undefined, UnknownAction> & Dispatch<UnknownAction>,
  value: string,
  index?: number
): void {
  value === "layout" ?
  dispatch( {
    type: "editor/selectToolbarDisplay",
    payload: {
      value: "layout"
    }
  } ) :
  dispatch( {
    type: "editor/selectToolbarDisplay",
    payload: (index !== null || true) ? { value: value, index: index } : { value: value }
  } );
}

/**
 * Exports the current layout on a given Fabric.js canvas to a base64 string.
 * Clears the active object on the canvas if it exists, then exports the canvas
 * content after a delay of 500ms, adjusting to the specified orientation.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas object to export.
 * @param {string} orient - The orientation of the export, typically "landscape" or "portrait".
 *                          This value determines the export dimensions.
 */
export const exportLayout = (canvasRef: fabric.Canvas | null): Promise<ServerResponse> => {
  return new Promise((resolve, reject) => {
    //@ts-ignore
    const canvas = canvasRef?.current;

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      return reject( new Error("Le canvas n'est pas prêt ou ses dimensions sont nulles.") );
    }

    canvas.discardActiveObject();

    const objectsToRemove = canvas.getObjects().filter(obj => obj.isSelectionBorder === true);
    objectsToRemove.forEach(obj => canvas.remove(obj));

    const performExport = async () => {
      try {
        const response = await exportAsBase64( canvasRef );
        resolve( response );
      } catch ( error ) {
        reject( error );
      }
    }

    canvas.once('after:render', performExport);

    canvas.requestRenderAll();
  });
};

/**
 * Exports the given fabric canvas instance as a base64 encoded image in JPEG format.
 *
 * @param {number} originalWidth - The original width to scale the canvas output.
 * @param {fabric.Canvas} fabricCanvasInstanceRef - The instance of fabric.Canvas to be exported.
 * @return {void} This function does not return a value; it opens a new tab with the base64 encoded image.
 */
async function exportAsBase64(
  fabricCanvasInstanceRef: fabric.Canvas,
): Promise<ServerResponse> {
  const selectedLayout = store.getState().editor.selectedLayout;
  const datas = store.getState().editor.currentData.layoutData;
  const params = store.getState().editor.currentData.paramGeneral
  const backgrounds = store.getState().editor.selectedBackground;
  const origninalScale = store.getState().editor.scale
  const layouts = store.getState().editor.layouts;

  const layout = layouts.find( ( layout ) => layout.id === selectedLayout );



  const background = backgrounds[ params.orient ]

  const currentData = datas.length > 0 && datas.find( data => data.id === selectedLayout )?.fabricElements;
      const parsedFabricObjects = currentData ? JSON.parse( currentData ).objects : [];

      await sanitizeCanvas( fabricCanvasInstanceRef, layout, origninalScale, selectedLayout, backgrounds, params.orient );

      const canvasBase64 = fabricCanvasInstanceRef.current.toDataURL( {
        format: "jpeg",
        quality: 1,
        multiplier: 1.5 / origninalScale,
        enableRetinaScaling: true,
        // @ts-ignore
        crossOrigin: 'anonymous'
      } );

      const finalResult = transformData( fabricCanvasInstanceRef.current, parsedFabricObjects, origninalScale, params, background, canvasBase64 );
   
      const uniqueImages = parsedFabricObjects
      .filter( img => img.type === "Image" && img.src )
      .map( img => ({
        data: {
          index: img.idx,
          base64: img.src
        },
      }) );

      console.log(finalResult);
      uniqueImages.forEach( img => console.log(img) );

      const postRequest = async ( url: string, body: object ): Promise<ServerResponse> => {

        const response = await fetch( url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify( body ),
        } );

        if ( !response.ok ) throw new Error( `Erreur HTTP : ${ response.statusText }` );
        return response.json();
      };

      try {
        const serverResponse = await postRequest(
          `${process.env.PREFIX_URL}/v2017/ajax/ajax.php?traitement=save-card-multimg-v2`,
          finalResult
        );

        const sortedImages = [...uniqueImages].sort((a, b) => a.index - b.index);

        for (const image of sortedImages) {
          try {
            await postRequest(
              `${process.env.PREFIX_URL}/v2017/ajax/ajax.php?traitement=save-card-multimg-v2`,
              image
            );
          } catch (error) {
            console.error(`Erreur (ID: ${image.index}) lors de l'envoi de l'image : `, error);
          }
        }

        return serverResponse;
    } catch ( error ) {
      console.error( "Erreur lors de l'export du canvas : ", error );
    }

  throw new Error();
}

/**
 * Enables an overlay on the web page that displays debug messages logged via `console.log`.
 *
 * This function creates a fixed-position debug console at the bottom of the browser window
 * to visually output all messages logged through `console.log`. The overlay offers an
 * auto-scrolling view of the logs and non-intrusive styling for readability.
 *
 * Once activated:
 * - `console.log` is overridden to display messages in the overlay along with logging to the browser console.
 * - Objects are serialized to JSON for display in the overlay.
 * - The overlay can display multi-line logs and maintains scroll at the bottom.
 *
 * @returns {Function} A cleanup function that restores the original `console.log` and
 *                     removes the overlay from the DOM when called.
 */
export const enableDebugConsoleOverlay = (): Function => {
  const logDiv = document.createElement( "div" );
  logDiv.setAttribute( "id", "console-log-overlay" );
  logDiv.style.position = "fixed";
  logDiv.style.bottom = "0";
  logDiv.style.width = "100%";
  logDiv.style.maxHeight = "200px";
  logDiv.style.overflowY = "auto";
  logDiv.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
  logDiv.style.color = "white";
  logDiv.style.fontSize = "12px";
  logDiv.style.padding = "10px";
  logDiv.style.zIndex = "9999";
  logDiv.style.fontFamily = "monospace";

  document.body.appendChild( logDiv );

  const originalLog = console.log;

  console.log = function ( ...args ) {
    originalLog.apply( console, args );

    const message = args.map( arg => (typeof arg === "object" ? JSON.stringify( arg ) : arg) ).join( " " );
    const line = document.createElement( "div" );
    line.textContent = message;
    logDiv.appendChild( line );

    logDiv.scrollTop = logDiv.scrollHeight;
  };

  return () => {
    document.body.removeChild( logDiv );
  };
};

/**
 * Asynchronous function to fetch an existing card.
 *
 * This function creates an XMLHttpRequest to send a POST request
 * to a specified server endpoint to retrieve a card. The request
 * includes specific headers to define its content type, indicate
 * it is an AJAX request, and accept a JSON response.

 */
export const fetchExistingCard = async () => {
  const xhr = new XMLHttpRequest();
  xhr.open(
    "POST",
    `${ process.env.PREFIX_URL }/v2017/ajax/ajax.php?traitement=get-card-multimg`,
    true
  );
  xhr.setRequestHeader( "Content-Type", "application/x-www-form-urlencoded" );
  xhr.setRequestHeader( "X-Requested-With", "XMLHttpRequest" );
  xhr.setRequestHeader( "Accept", "application/json" );
};

/**
 * Adds event listeners to prevent zooming behavior, both pinch-to-zoom and
 * zooming via control key with the mouse wheel, in the editor.
 *
 * The function attaches event listeners to:
 * - 'touchmove' events on the document to prevent multi-touch zoom interactions.
 * - 'wheel' events on the window to prevent zooming via the Ctrl key and mouse wheel.
 *
 * Both event listeners are configured with { passive: false } to ensure that
 * preventing the default behavior is possible.
 *
 * Returns a cleanup function that removes the added event listeners when invoked.
 *
 * @returns {void} A function for removing the event listeners added by this function.
 */
export const addEditorEventListeners = (): () => void  => {
  const preventZoom = ( event: TouchEvent ) => event.touches.length > 1 && event.preventDefault();
  const preventWheelZoom = ( event: WheelEvent ) => event.ctrlKey && event.preventDefault();

  document.addEventListener( "touchmove", preventZoom, { passive: false } );
  window.addEventListener( "wheel", preventWheelZoom, { passive: false } );

  return () => {
    document.removeEventListener( "touchmove", preventZoom );
    window.removeEventListener( "wheel", preventWheelZoom );
  };
};

/**
 * Attaches event listeners to the browser's visual viewport to track changes in its position
 * and updates the `setTop` state accordingly. Handles both `resize` and `scroll` events.
 *
 * @param {Object} setTop - A state updater function to set the `top` value. This typically updates a React state
 *   variable.
 * @returns {void} Cleanup function to remove the event listeners for `resize` and `scroll` from the visual
 *   viewport.
 */
export const handleEditorViewport = ( setTop: { (value: SetStateAction<number>): void; (arg0: number): any; }): () => void => {
  const updatePosition = () => setTop( window.visualViewport?.offsetTop || 0 );
  updatePosition();

  window.visualViewport?.addEventListener( "resize", updatePosition );
  window.visualViewport?.addEventListener( "scroll", updatePosition );

  return () => {
    window.visualViewport?.removeEventListener( "resize", updatePosition );
    window.visualViewport?.removeEventListener( "scroll", updatePosition );
  };
};

/**
 * Detects the operating system of the user's device based on the user agent string.
 *
 * @return {string} The name of the detected operating system. Possible values are:
 * "Android", "iOS", "Windows", "MacOS", "Linux", or "Inconnu" if the OS cannot be determined.
 */
export function detectOS() {
  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;

  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }
  if (/win/i.test(userAgent)) {
    return "Windows";
  }
  if (/mac/i.test(userAgent)) {
    return "MacOS";
  }
  if (/linux/i.test(userAgent)) {
    return "Linux";
  }

  return "Inconnu";
}

/**
 * Transforms an array of image and text objects into a structured data format.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas object containing the elements to transform.
 * @param {ImgCard[] | TextboxState[]} input - An array of objects representing either images or textboxes. Each object
 *   must contain properties specific to its type.
 * @param {number} scale - A numerical factor used to adjust the scaling of text properties.
 * @param {Object} params - Parameters defining general transformation properties, including layout and orientation
 *   information.
 * @param background
 * @param global
 * @param {number} params.page - The page index used for layout.
 * @param {string} params.layoutId - The identifier for the layout being transformed.
 * @param {boolean} params.isRounded - A boolean that indicates whether the layout should be rounded.
 * @param {string} params.carteId - The identifier for the card being processed.
 * @param {string} params.orient - The orientation of the layout (e.g., portrait or landscape).
 * @return {TransformedData} An object containing structured data, including base64 image sources, transformed imagery
 *   data, transformed text data, transformed text properties, and general layout parameters.
 */
function transformData( canvas: fabric.Canvas, input: ImgCard[] | TextboxState[], scale: number, params: ParamGeneral, background: Partial<BackgroundProps>, global: string ): TransformedData {
  const globalBase64: string = global;
  const dataImgCard: TransformedData["data"]["dataImgCard"] = [];
  const dataTxtCard: TransformedData["data"]["dataTxtCard"] = [];

  const paramGeneral: ParamGeneral = {
    uid: params.uid,
    page: params.page,
    layoutId: params.layoutId,
    isRounded: params.isRounded,
    isQRCode: params.isQRCode,
    isSignature: params.isSignature,
    isTextGenerator: params.isTextGenerator,
    carteId: params.carteId,
    orient: params.orient,
    bgColor: background.value,
    bgColorType: background.type,
    existingCanvasWidth: canvas.width,
    existingCanvasHeight: canvas.height
  };

  input.forEach( ( item ) => {
    if ( item.type === "Image" && item.src ) {
      const angles = [0, 90, 180, 270];

      const rotate = item.angle !== angles.includes(item.angle) ? findNearestAngle(angles, item.angle) : item.angle;

      dataImgCard.push( {
        index: item.idx,
        data: {
          x: item.left / scale|| 0,
          y: item.top / scale || 0,
          width: item.width,
          height: item.height,
          rotate: rotate || 0,
          scaleX: item.scaleY || 1,
          scaleY: item.scaleY  || 1,
          flipX: item.flipX || false,
          flipY: item.flipX || false
        }
      } );
    }

    if ( item.type === "Textbox" ) {
      dataTxtCard.push( {
        index: item.idx,
        value: item.text || "",
        properties: {
          textAlign: item.textAlign || "left",
          textFont: item.fontFamily || "Arial",
          textFontColor: item.fill || "#000000",
          textFontColorType: "color",
          textFontFamily: item.fontFamily || "Arial",
          textFontSize: String( item.fontSize / scale ),
          textVAlign: item.originY || "top",
        }
      } );
    }
  } );

  return {
    data: {
      globalBase64,
      dataImgCard,
      dataTxtCard,
      paramGeneral
    }
  };
}

/**
 * Finds the nearest angle from a set of predefined angles.
 * Normalizes the input angle to 0-360 range and finds the closest match
 * from the provided angles array.
 *
 * @param {number[]} angles - Array of allowed angles (e.g. [0, 90, 180, 270])
 * @param {number} currentAngle - The angle to find the nearest match for
 * @returns {number} The nearest allowed angle from the angles array
 *
 */
const findNearestAngle = (angles, currentAngle) => {
  const normalizedAngle = ((currentAngle % 360) + 360) % 360;

  return angles.reduce((nearest, angle) => {
    const currentDiff = Math.abs(normalizedAngle - nearest);
    const newDiff = Math.abs(normalizedAngle - angle);
    return newDiff < currentDiff ? angle : nearest;
  });
};

/**
 * Delays execution for a specified duration.
 *
 * @param ms The delay duration in milliseconds.
 * @returns A promise that resolves after the specified time.
 */
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const sanitizeCanvas = async (fabricCanvasInstanceRef: fabric.Canvas, layout, scale, selectedLayout, selectedBackground, orientation) => {
  const existingObjects = fabricCanvasInstanceRef.current?._objects;
  const dispatch = store.dispatch;

  if (existingObjects && existingObjects.length > 0) {
    const clonedObjects = await Promise.all(existingObjects.map(async (obj) => {
      try {
        if(obj.type === "image") {
        const properties = {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          originX: obj.originX,
          originY: obj.originY,
          angle: obj.angle,
          flipX: obj.flipX,
          flipY: obj.flipY,
          lockScalingX: obj.lockScalingX,
          lockScalingY: obj.lockScalingY,
          lockRotation: obj.lockRotation,
          hasControls: obj.hasControls,
          hasBorders: obj.hasBorders,
          lockMovementX: obj.lockMovementX,
          lockMovementY: obj.lockMovementY,
          hoverCursor: obj.hoverCursor,
          idx: obj.idx,
          placeholderImage: obj.placeholderImage,
          perPixelTargetFind: obj.perPixelTargetFind,
        };

        const clonedObj = await obj.clone();
        
        Object.assign(clonedObj, properties);
        const item = layout.elements[obj.idx];
        const borderRadius = parseFloat( item.style[ "border-radius" ] ) || 0
        configureFabricImage( clonedObj,
          item,
          scale,
          fabricCanvasInstanceRef,
          borderRadius,
          dispatch,
          selectedLayout,
          true );

          clonedObj.on( "mousedown", ( event ) => {
            event.e.stopPropagation();
            handleDisplayToolbar( dispatch, "image", obj.idx );
          } );
        
        return clonedObj;
      } else if (obj.type === "textbox") {
        const properties = {
          ...obj
        };

        const clonedObj = await obj.clone();
        
        Object.assign(clonedObj, properties);

        const item = layout.elements[obj.idx];
        configureTextBox(clonedObj, item, scale, fabricCanvasInstanceRef, dispatch, selectedLayout, obj.idx);
        
        return clonedObj;
      } else if (obj.type === "path") {
        const properties = {
          ...obj
        };

        const clonedObj = await obj.clone();
        
        Object.assign(clonedObj, properties);
        return clonedObj;
      }
      } catch (error) {
        console.error("Erreur lors du clonage de l'objet:", error);
        return null;
      }
    }));

    fabricCanvasInstanceRef.current?.clear();
    
    await backgroundCreation(
      selectedBackground,
      orientation,
      fabricCanvasInstanceRef.current,
      scale
    );

    clonedObjects.forEach(obj => {
      if (obj) {
        fabricCanvasInstanceRef.current?.add(obj);
      }
    });

    fabricCanvasInstanceRef.current?.requestRenderAll();
  } else {
    fabricCanvasInstanceRef.current?.clear();
    
    await backgroundCreation(
      selectedBackground,
      orientation,
      fabricCanvasInstanceRef.current,
      scale
    );
    
    fabricCanvasInstanceRef.current?.requestRenderAll();
  }

}